import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { formatDateTime } from '../utils/activities';

const POLL_INTERVAL = 5000;

// Подключение аккаунта внешнего приложения (app — mywhoosh | zwift): его заезды
// бэкенд забирает в одноименную папку (SyncMywhooshActivitiesJob,
// SyncZwiftActivitiesJob). API — /api/<app>, тексты — workouts.<app>.*.
// Пока идет синхронизация, статус опрашивается, а onImported зовется, когда
// забранных заездов стало больше, — список и дерево папок перечитываются,
// новые заезды видны сразу
const AppSyncPanel = ({ app, name, logo, onImported }) => {
  const { t, language } = useLanguage();
  const [status, setStatus] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const importedRef = useRef(null);
  const onImportedRef = useRef(onImported);
  onImportedRef.current = onImported;

  const applyStatus = useCallback((next) => {
    if (importedRef.current !== null && next.imported_count > importedRef.current) onImportedRef.current?.();
    importedRef.current = next.imported_count ?? importedRef.current;
    setStatus(next);
  }, []);

  useEffect(() => {
    axios.get(`/api/${app}`).then((res) => applyStatus(res.data)).catch(() => setStatus(null));
  }, [app, applyStatus]);

  useEffect(() => {
    if (!status?.syncing) return undefined;
    const timer = setTimeout(() => {
      axios.get(`/api/${app}`).then((res) => applyStatus(res.data)).catch(() => {});
    }, POLL_INTERVAL);
    return () => clearTimeout(timer);
  }, [app, status, applyStatus]);

  const run = async (request) => {
    setBusy(true);
    setError('');
    try {
      const res = await request();
      applyStatus(res.data);
      return true;
    } catch (err) {
      setError(err.response?.data?.error || t(`workouts.${app}.failed`));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const openForm = () => {
    setEmail(status?.email || '');
    setPassword('');
    setError('');
    setFormOpen(true);
  };

  const connect = async (event) => {
    event.preventDefault();
    if (await run(() => axios.post(`/api/${app}`, { email: email.trim(), password }))) {
      setFormOpen(false);
      setPassword('');
    }
  };

  const disconnect = () => {
    if (!window.confirm(t(`workouts.${app}.disconnectConfirm`))) return;
    run(() => axios.delete(`/api/${app}`));
  };

  if (!status) return null;

  const connected = status.connected && !status.auth_failed;

  return (
    <section className="workouts-upload app-sync-panel">
      <div className="app-sync-panel-head">
        <span className={`app-sync-logo app-sync-logo--${app}`} role="img" aria-label={name}>
          {logo}
        </span>
        <span className="workouts-muted">
          {connected
            ? t(`workouts.${app}.connectedAs`).replace('{{email}}', status.email)
            : t(`workouts.${app}.intro`)}
        </span>
      </div>

      {connected && (
        <p className="workouts-muted">
          {status.syncing
            ? t(`workouts.${app}.syncing`)
            : status.last_synced_at && t(`workouts.${app}.lastSync`)
              .replace('{{date}}', formatDateTime(status.last_synced_at, language))
              .replace('{{count}}', status.last_imported_count ?? 0)}
        </p>
      )}

      {status.auth_failed && <p className="workouts-errors">{t(`workouts.${app}.authFailed`)}</p>}
      {connected && status.last_error && !status.syncing && <p className="workouts-errors">{status.last_error}</p>}
      {error && <p className="workouts-errors">{error}</p>}

      {formOpen ? (
        <form className="app-sync-form" onSubmit={connect}>
          <label htmlFor={`${app}-email`}>{t(`workouts.${app}.email`)}</label>
          <input
            id={`${app}-email`}
            type="email"
            className="workouts-input app-sync-input"
            value={email}
            autoComplete="username"
            onChange={(event) => setEmail(event.target.value)}
            required
            autoFocus
          />
          <label htmlFor={`${app}-password`}>{t(`workouts.${app}.password`)}</label>
          <input
            id={`${app}-password`}
            type="password"
            className="workouts-input app-sync-input"
            value={password}
            autoComplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          <p className="workouts-field-hint">{t(`workouts.${app}.passwordHint`)}</p>
          <div className="app-sync-actions">
            <button type="submit" className="workouts-primary-btn" disabled={busy || !email.trim() || !password}>
              {busy ? t(`workouts.${app}.connecting`) : t(`workouts.${app}.connect`)}
            </button>
            <button type="button" className="workouts-secondary-btn" onClick={() => setFormOpen(false)} disabled={busy}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      ) : (
        <div className="app-sync-actions">
          {connected ? (
            <>
              <button
                type="button"
                className="workouts-secondary-btn"
                onClick={() => run(() => axios.post(`/api/${app}/sync`))}
                disabled={busy || status.syncing}
              >
                {t(`workouts.${app}.sync`)}
              </button>
              <button type="button" className="workouts-danger-btn" onClick={disconnect} disabled={busy}>
                {t(`workouts.${app}.disconnect`)}
              </button>
            </>
          ) : (
            <button type="button" className="workouts-primary-btn" onClick={openForm}>
              {status.auth_failed ? t(`workouts.${app}.reconnect`) : t(`workouts.${app}.connect`)}
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default AppSyncPanel;
